import { useEffect, useState } from "react";
import { compact, forEach, join, split, trim, uniq } from "lodash";
import useDebounce from "hooks/useDebounce";
import { AiccTtsVoiceResolutionResponse } from "types/dto/response/aiccLearningAssets";
import { fetchAiccTtsVoiceResolution } from "services/aiccLearningAssets";

const DEBOUNCE_MS = 500;

const KEY_SEPARATOR = "\u0000";

// An id's answer is a fact about the vendor, so it is cached across renders and languages.
const cache = new Map<string, Promise<AiccTtsVoiceResolutionResponse>>();

const resolve = (voiceId: string) => {
  let pending = cache.get(voiceId);
  if (!pending) {
    pending = (fetchAiccTtsVoiceResolution(voiceId) as unknown) as Promise<AiccTtsVoiceResolutionResponse>;
    cache.set(voiceId, pending);
  }
  return pending;
};

/** Per-id answers from the resolution endpoint, debounced because the ids come from what is being typed. */
export function useAiccTtsVoiceResolutions(voiceIds: string[]) {
  const [resolutions, setResolutions] = useState<Record<string, AiccTtsVoiceResolutionResponse>>({});
  // Keyed on the ids rather than the array, which is rebuilt on every keystroke.
  const key = useDebounce(join(uniq(compact(voiceIds.map(trim))), KEY_SEPARATOR), DEBOUNCE_MS);

  useEffect(() => {
    if (!key) {
      return undefined;
    }
    let isLive = true;
    forEach(split(key, KEY_SEPARATOR), (voiceId) => {
      resolve(voiceId)
        .then((resolution) => {
          if (isLive) {
            setResolutions((current) => ({ ...current, [voiceId]: resolution }));
          }
        })
        .catch(() => {
          // A failed request is not a verdict about the voice, so it is asked again next time.
          cache.delete(voiceId);
        });
    });
    return () => {
      isLive = false;
    };
  }, [key]);

  return resolutions;
}
